/**
 * Buntu Companion portal — what an end user sees at /companion.
 *
 * Deliberately not part of the admin Hub shell: an employee gets their own
 * device, updates and support view with no admin navigation. The desktop tray
 * loads this same URL, so any change here reaches both surfaces.
 *
 * Enrolled Windows trays inject a companion session token (and
 * __BUNTU_COMPANION_HOST__) so this page never asks for a password. Browser
 * visits to /companion still show the optional Hub sign-in card.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import CompanionUserApp from './CompanionUserApp';

const DEFAULT_ACCENT = '#253546';
const FALLBACK_LOGO = '/Buntu.png';

function apiBase(): string {
  return typeof window !== 'undefined' ? `${window.location.origin}/api/v1` : '/api/v1';
}

/** ?tenant=<code> lets the sign-in card brand itself before a user is known. */
function tenantHint(): string {
  try {
    return new URLSearchParams(window.location.search).get('tenant') || '';
  } catch {
    return '';
  }
}

function hostQueryFlag(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('host') === '1';
  } catch {
    return false;
  }
}

function isWebViewHost(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Edg\/|WebView|WV/i.test(ua) && hostQueryFlag();
}

/**
 * The desktop host registers with the agent's device identity and injects the
 * resulting session token before this page loads, so the tray shows the portal
 * straight away. Injection keeps the token out of the URL and browser history.
 */
function hostSessionToken(): string | null {
  const injected = (window as unknown as { __BUNTU_COMPANION_TOKEN__?: unknown }).__BUNTU_COMPANION_TOKEN__;
  return typeof injected === 'string' && injected.length > 0 ? injected : null;
}

function isCompanionHostShell(): boolean {
  const flag = (window as unknown as { __BUNTU_COMPANION_HOST__?: unknown }).__BUNTU_COMPANION_HOST__;
  return flag === true || hostQueryFlag() || isWebViewHost();
}

interface BrandingPayload {
  tenant?: { name?: string | null } | null;
  branding?: {
    theme_color?: string | null;
    logo_url?: string | null;
    login_message?: string | null;
  } | null;
}

const CompanionPortal: React.FC = () => {
  const { isAuthenticated, token, user, login, logout } = useAuth();
  const [hostToken, setHostToken] = useState<string | null>(() => hostSessionToken());
  const [hostShell] = useState<boolean>(() => isCompanionHostShell());
  const [branding, setBranding] = useState<BrandingPayload | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Host may inject the token slightly after first paint, or reload after
  // background registration succeeds — keep watching instead of locking onto "".
  useEffect(() => {
    if (hostToken) return;
    const tick = () => {
      const next = hostSessionToken();
      if (next) setHostToken(next);
    };
    tick();
    const id = window.setInterval(tick, 1500);
    return () => window.clearInterval(id);
  }, [hostToken]);

  // Enrolled tray without a token yet: soft-reload a few times so a late
  // sessionKeeper registration can re-inject and open the portal.
  useEffect(() => {
    if (!hostShell || hostToken || isAuthenticated) return;
    let attempts = 0;
    const id = window.setInterval(() => {
      attempts += 1;
      if (hostSessionToken()) {
        window.clearInterval(id);
        window.location.reload();
        return;
      }
      if (attempts >= 8) window.clearInterval(id);
      else if (attempts % 3 === 0) window.location.reload();
    }, 2500);
    return () => window.clearInterval(id);
  }, [hostShell, hostToken, isAuthenticated]);

  useEffect(() => {
    const hint = tenantHint();
    const query = hint ? `?tenant=${encodeURIComponent(hint)}` : '';
    fetch(`${apiBase()}/auth/branding${query}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) setBranding(data);
      })
      .catch(() => {
        // Branding is cosmetic; the portal works without it.
      });
  }, []);

  const accent = branding?.branding?.theme_color || DEFAULT_ACCENT;
  const logo = branding?.branding?.logo_url || FALLBACK_LOGO;
  const tenantName = branding?.tenant?.name || '';
  const title = tenantName ? `${tenantName} Companion` : 'Buntu Companion';

  const onLogoError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    (event.target as HTMLImageElement).src = FALLBACK_LOGO;
  };

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setError('');
      setBusy(true);
      try {
        await login(username.trim(), password);
        setPassword('');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Sign-in failed');
      } finally {
        setBusy(false);
      }
    },
    [login, username, password]
  );

  // Desktop host on an enrolled device: never show username/password.
  if (!isAuthenticated && !hostToken && hostShell) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 p-4">
        <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-6 shadow-sm text-center">
          <img src={logo} alt="" onError={onLogoError} className="mx-auto mb-3 h-12 w-12 rounded-lg object-contain" />
          <h1 className="text-lg font-semibold text-slate-800">{title}</h1>
          <p className="mt-2 text-sm text-slate-500">
            Connecting with your enrolled device…
          </p>
          <p className="mt-1 text-xs text-slate-400">
            Using the Windows account signed in on this PC. No Hub password needed.
          </p>
          <div className="mt-5 mx-auto h-1.5 w-40 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full w-1/2 animate-pulse rounded-full" style={{ backgroundColor: accent }} />
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated && !hostToken) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 p-4">
        <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-6 flex flex-col items-center text-center">
            <img src={logo} alt="" onError={onLogoError} className="mb-3 h-12 w-12 rounded-lg object-contain" />
            <h1 className="text-lg font-semibold text-slate-800">{title}</h1>
            <p className="mt-1 text-sm text-slate-500">
              {branding?.branding?.login_message ||
                'Sign in to check your device, updates and support requests.'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label htmlFor="companion-username" className="mb-1 block text-xs font-medium text-slate-600">
                Email or username
              </label>
              <input
                id="companion-username"
                type="text"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 outline-none focus:border-slate-400"
              />
            </div>
            <div>
              <label htmlFor="companion-password" className="mb-1 block text-xs font-medium text-slate-600">
                Password
              </label>
              <input
                id="companion-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 outline-none focus:border-slate-400"
              />
            </div>

            {error && (
              <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={busy || !username.trim() || !password}
              style={{ backgroundColor: accent }}
              className="w-full rounded-md px-3 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="mt-4 text-center text-xs text-slate-400">
            On an enrolled PC, open Buntu from the system tray — it signs you in as the Windows user automatically.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
        <div className="flex items-center gap-2">
          <img src={logo} alt="" onError={onLogoError} className="h-8 w-8 rounded-md object-contain" />
          <div>
            <div className="text-sm font-semibold text-slate-800">{title}</div>
            {user?.username || user?.email ? (
              <div className="text-xs text-slate-500">{user.full_name || user.username || user.email}</div>
            ) : null}
          </div>
        </div>
        {!hostToken && (
          <button
            type="button"
            onClick={() => logout()}
            className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
          >
            <LogOut size={14} />
            Sign out
          </button>
        )}
      </header>
      <CompanionUserApp hubToken={token} sessionToken={hostToken} />
    </div>
  );
};

export default CompanionPortal;
